import { FC, MouseEvent, type ReactElement } from "react"
import {
	CSSProperties,
	PropsWithChildren,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react"
import { useTranslation } from "react-i18next"
import { Icon } from "./icons/Icon"
import clsx from "clsx"
import { analytics } from "@/telemetry/amplitude"
import { useAtomValue } from "jotai"
import { projectAtom } from "@/store/project/projects-state"

type CustomExpandView = (
	isExpanded: boolean | undefined,
	handler: (e: React.MouseEvent) => void,
) => ReactElement

export type ExpandablePanelCommonProps = PropsWithChildren<{
	name: string
	compact?: boolean
	fullSizeContent?: boolean
	disabled?: boolean
	hideSeparator?: boolean
	accessoryView?: React.ReactNode
	customNameView?: React.ReactNode
	customExpandView?: CustomExpandView
	className?: string
	isSticky?: boolean
	flex?: boolean
	onContextMenu?: (e: MouseEvent) => void
	expandedHeight?: CSSProperties["height"]
	isExperimental?: boolean
}>

type ExpandablePanelProps = ExpandablePanelCommonProps & {
	isExpanded: boolean
	onToggleExpanded?: (expanded: boolean) => void
}

export const ExpandablePanel: FC<ExpandablePanelProps> = ({
	name,
	isExpanded,
	compact,
	className,
	fullSizeContent,
	onToggleExpanded,
	disabled,
	hideSeparator,
	children,
	accessoryView,
	customNameView,
	customExpandView,
	isSticky,
	onContextMenu,
	expandedHeight = "auto",
	isExperimental,
	flex,
}) => {
	const { t } = useTranslation("components")
	const [height, setHeight] = useState<CSSProperties["height"]>(isExpanded ? expandedHeight : 0)
	const panelRef = useRef<HTMLDivElement>(null)
	const contentRef = useRef<HTMLDivElement>(null)
	const hasMountedRef = useRef(false)
	const animationDuration = 200
	const project = useAtomValue(projectAtom)

	const handleClick = (e: MouseEvent) => {
		analytics.trackEvent(
			{
				label: isExpanded ? "collapse_group" : "expand_group",
				type: "click",
				customProperties: project?.id ? { projectId: project.id } : undefined,
			},
			e.target,
		)
		onToggleExpanded?.(!isExpanded)
	}

	useLayoutEffect(() => {
		if (isExpanded && height === expandedHeight) {
			return
		}

		const currentHeight = contentRef.current?.clientHeight || 0
		const targetHeight = isExpanded ? contentRef.current?.scrollHeight || 0 : 0

		// Set height to current pixel value so we can animate
		setHeight(currentHeight)

		// Animate to target height
		requestAnimationFrame(() => {
			setHeight(targetHeight)
		})

		// Reset height to "auto" when transition completes so
		// that the element height will update when children heights change
		const timeout = setTimeout(() => {
			setHeight(isExpanded ? expandedHeight : 0)
		}, animationDuration)

		return () => {
			clearTimeout(timeout)
		}
	}, [isExpanded]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!hasMountedRef.current) {
			hasMountedRef.current = true
			return
		}

		if (!isExpanded) {
			return
		}

		const timeout = window.setTimeout(() => {
			const panel = panelRef.current
			if (!panel) {
				return
			}

			let scrollParent = panel.parentElement
			while (scrollParent) {
				const overflowY = window.getComputedStyle(scrollParent).overflowY
				const isScrollable =
					(overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
					scrollParent.scrollHeight > scrollParent.clientHeight

				if (isScrollable) {
					break
				}

				scrollParent = scrollParent.parentElement
			}

			if (!scrollParent) {
				return
			}

			const panelRect = panel.getBoundingClientRect()
			const scrollParentRect = scrollParent.getBoundingClientRect()
			const padding = 12
			const availableHeight = scrollParentRect.height - padding * 2

			if (panelRect.top < scrollParentRect.top + padding) {
				scrollParent.scrollBy({
					top: panelRect.top - scrollParentRect.top - padding,
					behavior: "smooth",
				})
				return
			}

			// If the expanded panel is taller than the visible area, aligning its bottom
			// would hide the start of the panel. Leave it anchored where the user opened it.
			if (panelRect.height > availableHeight) {
				return
			}

			if (panelRect.bottom > scrollParentRect.bottom - padding) {
				scrollParent.scrollBy({
					top: panelRect.bottom - scrollParentRect.bottom + padding,
					behavior: "smooth",
				})
			}
		}, animationDuration + 20)

		return () => {
			window.clearTimeout(timeout)
		}
	}, [isExpanded])

	const renderNameView = () => {
		if (customNameView) {
			return customNameView
		}

		return (
			<>
				<div
					className={clsx("flex select-none text-left", {
						"text-xs": compact,
						"text-sm": !compact,
					})}
				>
					{name}
				</div>
			</>
		)
	}

	const renderExperimentalView = () => {
		return (
			<div className="ml-2 rounded-sm border border-base-600 px-2 py-1 text-xxs text-base-400">
				{t("expandablePanel.experimental")}
			</div>
		)
	}

	const renderExpandIconView = () => {
		if (customExpandView) {
			return customExpandView?.(isExpanded, handleClick)
		}

		return (
			<Icon
				name="expand_right"
				className="ml-[-4px] transition-transform"
				onClick={handleClick}
				style={{
					transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
				}}
				size={24}
			/>
		)
	}

	const renderHeader = () => {
		return (
			<div
				className={clsx(
					"flex w-full flex-row items-center justify-between rounded-sm text-base-200 hover:text-base-100 data-[selected]:bg-base-700",
					{
						"px-2": !fullSizeContent,
					},
					className,
				)}
				onContextMenu={onContextMenu}
			>
				<div
					role="button"
					aria-expanded={isExpanded}
					aria-label={t("expandablePanel.togglePanel", { name: name })}
					tabIndex={0}
					className="flex w-full flex-row items-center justify-start"
					onClick={handleClick}
				>
					{renderExpandIconView()}
					{renderNameView()}
					{isExperimental && renderExperimentalView()}
				</div>
				{accessoryView}
			</div>
		)
	}

	return (
		<div
			ref={panelRef}
			aria-label={t("expandablePanel.ariaPanel", { name })}
			className={clsx(
				isSticky && "sticky",
				!hideSeparator && "border-b border-base-900",
				"flex w-full flex-col items-start overflow-hidden",
				!compact && "py-2",
				flex && "flex-1",
				!flex && "flex-none",
			)}
		>
			{renderHeader()}
			<div
				ref={contentRef}
				className="transition-all aria-disabled:pointer-events-none aria-disabled:opacity-50"
				aria-disabled={disabled}
				style={{
					height: height,
					width: "100%",
					transitionDuration: `${animationDuration}ms`,
					overflow: "hidden",
					contain: "content",
				}}
			>
				<div
					aria-hidden={!isExpanded}
					className={!fullSizeContent ? "px-3 pb-1" : ""}
					style={{ height }}
				>
					{children}
				</div>
			</div>
		</div>
	)
}
